/**
 * PetsAnimalsServicesDisplay - Main display for Pets & Animals category
 * Shows pet ads, services, and providers with distinct styling
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCountry } from '../../context/CountryContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Search, MapPin, Star, ChevronRight, Heart, Eye,
  Dog, Cat, Bird, Fish, ShoppingBag, Stethoscope,
  Scissors, GraduationCap, Home, Clock, Sparkles,
  Shield, CheckCircle, Loader2, ArrowRight, Zap,
  Package, Calendar
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category icons mapping
const CATEGORY_ICONS = {
  'dogs': Dog,
  'cats': Cat,
  'birds': Bird,
  'fish-aquariums': Fish,
  'livestock': ShoppingBag,
  'pet-food': Package,
  'pet-accessories': ShoppingBag,
  'pet-services': Stethoscope,
};

// Provider type icons
const PROVIDER_ICONS = {
  'VET': Stethoscope,
  'GROOMER': Scissors,
  'WALKER': Dog,
  'SITTER': Home,
  'TRAINER': GraduationCap,
  'BOARDING': Home,
  'AQUARIUM': Fish,
};

// Badge Component
function TrustBadge({ badge }) {
  const colors = {
    green: 'bg-green-100 text-green-700',
    blue: 'bg-blue-100 text-blue-700',
    purple: 'bg-purple-100 text-purple-700',
    gold: 'bg-amber-100 text-amber-700',
    teal: 'bg-teal-100 text-teal-700',
    orange: 'bg-orange-100 text-orange-700',
    yellow: 'bg-yellow-100 text-yellow-700',
  };
  
  return (
    <Badge className={`text-xs ${colors[badge.color] || colors.gray}`}>
      <span className="mr-1">{badge.icon}</span>
      {badge.label}
    </Badge>
  );
}

// ==================== AD CARD (Classified Listing Style) ====================
function AdCard({ ad }) {
  const navigate = useNavigate();
  // Derive currency from the listing's country so LK ads always show Rs. even
  // when the (mostly missing) `currency` field is not set on the doc.
  const adCountry = (ad.country || '').toUpperCase();
  const currencySymbol = adCountry === 'LK' || adCountry === 'SL' || ad.currency === 'LKR'
    ? 'Rs.'
    : adCountry === 'UK' || adCountry === 'GB' || ad.currency === 'GBP'
      ? '£'
      : (ad.currency === 'LKR' ? 'Rs.' : '£');
  
  return (
    <Card 
      className="group hover:shadow-xl transition-all cursor-pointer overflow-hidden border-0 bg-white"
      onClick={() => navigate(`/listing/${ad.id}`)}
      data-testid={`ad-card-${ad.id}`}
    >
      {/* Image with overlay */}
      <div className="relative h-44 bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden">
        {ad.images?.[0] ? (
          <img 
            src={ad.images[0]} 
            alt={ad.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Package className="w-12 h-12 text-gray-300" />
          </div>
        )}
        
        {/* Featured badge */}
        {ad.featured && (
          <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs py-1 px-2 flex items-center justify-center gap-1">
            <Star className="w-3 h-3 fill-white" />
            FEATURED
          </div>
        )}
        
        {/* Heart button */}
        <button 
          className="absolute top-2 right-2 w-8 h-8 bg-white/90 backdrop-blur rounded-full flex items-center justify-center hover:bg-white hover:scale-110 transition-all shadow-sm"
          onClick={(e) => {
            e.stopPropagation();
            toast.success('Saved to favorites');
          }}
        >
          <Heart className="w-4 h-4 text-gray-600" />
        </button>
        
        {/* Condition badge */}
        <Badge className={`absolute bottom-2 left-2 text-xs ${
          ad.condition === 'New' ? 'bg-green-500 text-white' : 
          ad.condition?.includes('Excellent') ? 'bg-blue-500 text-white' :
          'bg-gray-500 text-white'
        }`}>
          {ad.condition}
        </Badge>
      </div>
      
      <CardContent className="p-3">
        <h3 className="font-medium text-gray-900 line-clamp-2 text-sm mb-2 group-hover:text-indigo-600 transition-colors">
          {ad.title}
        </h3>
        
        {/* Price */}
        <div className="flex items-center justify-between mb-2">
          <span className="text-lg font-bold text-indigo-600">
            {ad.price > 0 ? `${currencySymbol}${ad.price.toLocaleString()}` : 'Free'}
          </span>
          {ad.price > 0 && (
            <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
              Negotiable
            </Badge>
          )}
        </div>
        
        {/* Location & Views */}
        <div className="flex items-center justify-between text-xs text-gray-500">
          <div className="flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            <span>{ad.location}</span>
          </div>
          <div className="flex items-center gap-1">
            <Eye className="w-3 h-3" />
            <span>{ad.views || 0}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ==================== SERVICE CARD (Professional Service Style) ====================
function ServiceCard({ provider }) {
  const navigate = useNavigate();
  const Icon = PROVIDER_ICONS[provider.provider_type] || Stethoscope;
  const currencySymbol = provider.country === 'LK' ? 'Rs.' : '£';
  
  // Get starting price from services
  const startingPrice = provider.starting_price || (provider.country === 'LK' ? 800 : 12);
  
  // Background gradient based on provider type
  const gradients = {
    'VET': 'from-emerald-500 to-teal-600',
    'GROOMER': 'from-pink-500 to-rose-600',
    'WALKER': 'from-blue-500 to-indigo-600',
    'SITTER': 'from-purple-500 to-violet-600',
    'TRAINER': 'from-amber-500 to-orange-600',
    'BOARDING': 'from-cyan-500 to-blue-600',
    'AQUARIUM': 'from-blue-400 to-cyan-500',
  };
  
  const gradient = gradients[provider.provider_type] || 'from-indigo-500 to-purple-600';
  
  return (
    <div 
      className="group relative bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 cursor-pointer border border-gray-100"
      onClick={() => navigate(`/pets/book/${provider.provider_id}`)}
      data-testid={`service-card-${provider.provider_id}`}
    >
      {/* Gradient header */}
      <div className={`h-24 bg-gradient-to-r ${gradient} relative overflow-hidden`}>
        {/* Pattern overlay */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full" style={{
            backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
            backgroundSize: '20px 20px'
          }} />
        </div>
        
        {/* Icon */}
        <div className="absolute -bottom-6 left-4 w-16 h-16 bg-white rounded-2xl shadow-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
          <Icon className="w-8 h-8 text-gray-700" />
        </div>
        
        {/* Verified badge */}
        {provider.verification === 'FULLY_VERIFIED' && (
          <div className="absolute top-3 right-3 bg-white/20 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
            <CheckCircle className="w-3 h-3" />
            Verified
          </div>
        )}
      </div>
      
      <CardContent className="pt-10 pb-4 px-4">
        {/* Provider name */}
        <h3 className="font-bold text-gray-900 mb-1 group-hover:text-indigo-600 transition-colors">
          {provider.display_name}
        </h3>
        <p className="text-sm text-gray-500 mb-3 line-clamp-1">{provider.tagline}</p>
        
        {/* Rating & Location */}
        <div className="flex items-center gap-3 mb-3">
          {provider.rating_avg && (
            <div className="flex items-center gap-1 bg-amber-50 px-2 py-1 rounded-full">
              <Star className="w-3 h-3 text-amber-500 fill-amber-500" />
              <span className="text-xs font-semibold text-amber-700">{provider.rating_avg}</span>
              <span className="text-xs text-amber-600">({provider.rating_count})</span>
            </div>
          )}
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <MapPin className="w-3 h-3" />
            <span>{provider.location?.city}</span>
          </div>
        </div>
        
        {/* Service modes */}
        <div className="flex flex-wrap gap-1 mb-4">
          {provider.service_modes?.slice(0, 3).map(mode => (
            <span key={mode} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full capitalize">
              {mode.replace('_', ' ')}
            </span>
          ))}
        </div>
        
        {/* Price & CTA */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          <div>
            <span className="text-xs text-gray-500">From</span>
            <p className="text-lg font-bold text-gray-900">{currencySymbol}{startingPrice}</p>
          </div>
          <Button 
            size="sm" 
            className={`bg-gradient-to-r ${gradient} hover:opacity-90 text-white shadow-lg`}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/pets/book/${provider.provider_id}`);
            }}
          >
            Book Now
            <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </CardContent>
    </div>
  );
}

// Provider Card Component (alternate simpler style)
function ProviderCard({ provider, onBook }) {
  const navigate = useNavigate();
  const Icon = PROVIDER_ICONS[provider.provider_type] || Stethoscope;
  const currencySymbol = provider.country === 'LK' ? 'Rs.' : '£';
  
  return (
    <Card 
      className="hover:shadow-lg transition-all cursor-pointer"
      onClick={() => navigate(`/pets/provider/${provider.provider_id}`)}
      data-testid={`provider-card-${provider.provider_id}`}
    >
      <CardContent className="p-4">
        {/* Header */}
        <div className="flex items-start gap-3 mb-3">
          <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
            <Icon className="w-6 h-6 text-indigo-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 truncate">{provider.display_name}</h3>
            <p className="text-sm text-gray-500 truncate">{provider.tagline}</p>
          </div>
          {provider.verification === 'FULLY_VERIFIED' && (
            <Badge className="bg-green-100 text-green-700">
              <CheckCircle className="w-3 h-3 mr-1" />
              Verified
            </Badge>
          )}
        </div>
        
        {/* Rating & Location */}
        <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
          {provider.rating_avg && (
            <div className="flex items-center gap-1">
              <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
              <span className="font-medium">{provider.rating_avg}</span>
              <span className="text-gray-400">({provider.rating_count})</span>
            </div>
          )}
          <div className="flex items-center gap-1">
            <MapPin className="w-4 h-4 text-gray-400" />
            <span>{provider.location?.city}</span>
          </div>
        </div>
        
        {/* Service Modes */}
        <div className="flex flex-wrap gap-1 mb-3">
          {provider.service_modes?.map(mode => (
            <Badge key={mode} variant="outline" className="text-xs capitalize">
              {mode.replace('_', ' ')}
            </Badge>
          ))}
        </div>
        
        {/* Badges */}
        {provider.badges?.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {provider.badges.slice(0, 3).map(badge => (
              <Badge key={badge} variant="secondary" className="text-xs">
                {badge.replace('_', ' ')}
              </Badge>
            ))}
          </div>
        )}
        
        {/* Actions */}
        <div className="flex gap-2 mt-3">
          <Button 
            variant="outline" 
            size="sm" 
            className="flex-1"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/pets/provider/${provider.provider_id}`);
            }}
          >
            View Profile
          </Button>
          <Button 
            size="sm" 
            className="flex-1 bg-indigo-600 hover:bg-indigo-700"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/pets/book/${provider.provider_id}`);
            }}
            data-testid={`book-provider-${provider.provider_id}`}
          >
            Book
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Listing Card Component
function ListingCard({ listing }) {
  const navigate = useNavigate();
  const currencySymbol = listing.currency === 'LKR' ? 'Rs.' : '£';
  const isAdoption = listing.listing_type === 'adoption';
  
  return (
    <Card 
      className="hover:shadow-lg transition-all cursor-pointer overflow-hidden"
      onClick={() => navigate(`/pets/listing/${listing.listing_id}`)}
      data-testid={`listing-card-${listing.listing_id}`}
    >
      {/* Image */}
      {listing.media_urls?.[0] && (
        <div className="relative h-48 bg-gray-100">
          <img 
            src={listing.media_urls[0]} 
            alt={listing.title}
            className="w-full h-full object-cover"
          />
          {isAdoption && (
            <Badge className="absolute top-2 left-2 bg-pink-500">
              <Heart className="w-3 h-3 mr-1" />
              Adoption
            </Badge>
          )}
          {listing.moderation?.warning && (
            <Badge className="absolute top-2 right-2 bg-amber-500 text-white">
              {listing.moderation.warning}
            </Badge>
          )}
        </div>
      )}
      
      <CardContent className="p-4">
        <h3 className="font-semibold text-gray-900 mb-1 line-clamp-2">{listing.title}</h3>
        
        {/* Pet Details */}
        <div className="flex flex-wrap gap-2 text-sm text-gray-600 mb-2">
          {listing.pet_details?.breed && (
            <span>{listing.pet_details.breed}</span>
          )}
          {listing.pet_details?.age_months && (
            <span>• {Math.floor(listing.pet_details.age_months / 12)}y {listing.pet_details.age_months % 12}m</span>
          )}
          {listing.pet_details?.sex && (
            <span>• {listing.pet_details.sex}</span>
          )}
        </div>
        
        {/* Vaccination Badge */}
        {listing.pet_details?.vaccinated === 'yes' && (
          <Badge className="bg-green-100 text-green-700 text-xs mb-2">
            💉 Vaccination Records
          </Badge>
        )}
        
        {/* Location */}
        <div className="flex items-center gap-1 text-sm text-gray-500 mb-3">
          <MapPin className="w-4 h-4" />
          <span>{listing.location?.city}</span>
        </div>
        
        {/* Price */}
        <div className="flex items-center justify-between">
          {listing.price ? (
            <span className="text-lg font-bold text-indigo-600">
              {currencySymbol}{listing.price?.toLocaleString()}
            </span>
          ) : (
            <Badge className="bg-pink-100 text-pink-700">Free to Good Home</Badge>
          )}
          <span className="text-xs text-gray-400">{listing.views} views</span>
        </div>
      </CardContent>
    </Card>
  );
}

// Category Card Component
function CategoryCard({ category, onClick }) {
  const Icon = CATEGORY_ICONS[category.slug] || ShoppingBag;
  
  return (
    <div 
      onClick={() => onClick(category.slug)}
      className="flex flex-col items-center p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-all cursor-pointer border border-gray-100"
      data-testid={`category-${category.slug}`}
    >
      <div className="w-14 h-14 rounded-full bg-indigo-100 flex items-center justify-center mb-2">
        <Icon className="w-7 h-7 text-indigo-600" />
      </div>
      <span className="text-sm font-medium text-center">{category.name}</span>
      {category.warning && (
        <Badge variant="outline" className="text-xs mt-1 bg-amber-50 text-amber-700">
          Restricted
        </Badge>
      )}
    </div>
  );
}

// Service Type Card for Pet Services
function ServiceTypeCard({ service, onClick }) {
  const icons = {
    'veterinary': Stethoscope,
    'grooming': Scissors,
    'training': GraduationCap,
    'walking': Dog,
    'boarding': Home,
    'sitting': Home,
    'vaccination': Shield,
    'microchipping': Shield,
  };
  const Icon = icons[service] || Stethoscope;
  
  const labels = {
    'veterinary': 'Vet & Clinics',
    'grooming': 'Grooming',
    'training': 'Training',
    'walking': 'Dog Walking',
    'boarding': 'Boarding',
    'sitting': 'Pet Sitting',
    'vaccination': 'Vaccinations',
    'microchipping': 'Microchipping',
  };
  
  return (
    <Button 
      variant="outline" 
      className="flex items-center gap-2 whitespace-nowrap"
      onClick={() => onClick(service)}
    >
      <Icon className="w-4 h-4" />
      {labels[service] || service}
    </Button>
  );
}

// Main Component
export default function PetsAnimalsServicesDisplay() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { country: visitorCountry } = useCountry();

  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [providers, setProviders] = useState([]);
  const [listings, setListings] = useState([]);
  const [ads, setAds] = useState([]);
  const [countryConfig, setCountryConfig] = useState(null);
  
  const [activeTab, setActiveTab] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || null);
  const [selectedService, setSelectedService] = useState(searchParams.get('service') || null);
  const [searchQuery, setSearchQuery] = useState('');
  const [city, setCity] = useState('');
  // Default to visitor's country context (SL → LK, UK → UK) instead of hard-coding UK.
  const [country, setCountry] = useState(() => {
    const v = (visitorCountry || 'UK').toUpperCase();
    return v === 'SL' ? 'LK' : v;
  });

  // Keep in sync if visitor changes country via footer.
  useEffect(() => {
    const v = (visitorCountry || 'UK').toUpperCase();
    setCountry(v === 'SL' ? 'LK' : v);
  }, [visitorCountry]);
  
  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${API_URL}/api/pets/categories?country=${country}`);
        const data = await response.json();
        if (data.success) {
          setCategories(data.categories || []);
          setCountryConfig({
            currency: data.currency,
            currency_symbol: data.currency_symbol,
          });
        }
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      }
    };
    fetchCategories();
  }, [country]);
  
  // Fetch all data (providers, ads, listings)
  useEffect(() => {
    const fetchAllData = async () => {
      setLoading(true);
      try {
        // Fetch providers
        let providerUrl = `${API_URL}/api/pets/providers?country=${country}`;
        if (selectedService) {
          const serviceToProvider = {
            'veterinary': 'VET',
            'grooming': 'GROOMER',
            'walking': 'WALKER',
            'sitting': 'SITTER',
            'training': 'TRAINER',
            'boarding': 'BOARDING',
          };
          const providerType = serviceToProvider[selectedService];
          if (providerType) {
            providerUrl += `&provider_type=${providerType}`;
          }
        }
        if (city) {
          providerUrl += `&city=${encodeURIComponent(city)}`;
        }
        
        // Fetch ads from main listings collection. NOTE: the main listings API
        // expects `country_code` (not `country`) — silently returns everything
        // otherwise, which is what caused UK Devon/Somerset listings to leak
        // onto the Sri Lanka page.
        let adsUrl = `${API_URL}/api/listings/?category=pets-animals&country_code=${country}`;
        if (selectedCategory) {
          adsUrl += `&subcategory=${selectedCategory}`;
        }
        
        const [providerRes, adsRes] = await Promise.all([
          fetch(providerUrl),
          fetch(adsUrl),
        ]);
        
        const providerData = await providerRes.json();
        const adsData = await adsRes.json();
        
        if (providerData.success) {
          setProviders(providerData.providers || []);
        }
        
        // Ads come from main listings API
        setAds(adsData.listings || adsData || []);
        
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchAllData();
  }, [country, selectedService, selectedCategory, city]);
  
  // Handle category click
  const handleCategoryClick = (slug) => {
    setSelectedCategory(slug === selectedCategory ? null : slug);
    if (slug === 'pet-services') {
      setActiveTab('services');
    }
    setSearchParams(slug ? { category: slug } : {});
  };
  
  // Handle service click
  const handleServiceClick = (service) => {
    setSelectedService(service === selectedService ? null : service);
  };
  
  // Filter ads by subcategory
  const filteredAds = selectedCategory 
    ? ads.filter(ad => ad.subcategory === selectedCategory)
    : ads;
  
  // Pet services from categories
  const petServicesCategory = categories.find(c => c.slug === 'pet-services');
  const availableServices = petServicesCategory?.services || [
    'veterinary', 'grooming', 'training', 'walking', 'boarding', 'sitting'
  ];
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      {/* Hero Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-8 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <Dog className="w-10 h-10" />
            <div>
              <h1 className="text-3xl font-bold">Pets & Animals</h1>
              <p className="text-indigo-200">Pet services, adoption, and supplies in {country === 'UK' ? 'United Kingdom' : 'Sri Lanka'}</p>
            </div>
          </div>
          
          {/* Search Bar */}
          <div className="flex flex-col md:flex-row gap-3 mt-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input 
                placeholder="Search services, providers, or pets..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-white text-gray-900"
              />
            </div>
            <div className="flex gap-2">
              <Select value={country} onValueChange={setCountry}>
                <SelectTrigger className="w-32 bg-white text-gray-900">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="UK">🇬🇧 UK</SelectItem>
                  <SelectItem value="LK">🇱🇰 Sri Lanka</SelectItem>
                </SelectContent>
              </Select>
              <Input 
                placeholder="City"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="w-40 bg-white text-gray-900"
              />
            </div>
          </div>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Category Grid */}
        <div className="grid grid-cols-4 sm:grid-cols-7 gap-3 mb-6">
          {categories.map(category => (
            <CategoryCard 
              key={category.slug}
              category={category}
              onClick={handleCategoryClick}
            />
          ))}
        </div>
        
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList className="bg-white shadow-sm">
            <TabsTrigger value="all" className="flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              All
            </TabsTrigger>
            <TabsTrigger value="services" className="flex items-center gap-2">
              <Stethoscope className="w-4 h-4" />
              Services
            </TabsTrigger>
            <TabsTrigger value="ads" className="flex items-center gap-2">
              <ShoppingBag className="w-4 h-4" />
              Ads & Listings
            </TabsTrigger>
          </TabsList>
          
          {/* ALL Tab - Shows both */}
          <TabsContent value="all" className="mt-6">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
              </div>
            ) : (
              <>
                {/* Services Section */}
                {providers.length > 0 && (
                  <div className="mb-10">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
                          <Zap className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <h2 className="text-xl font-bold text-gray-900">Pet Services</h2>
                          <p className="text-sm text-gray-500">Book professional pet care</p>
                        </div>
                      </div>
                      <Button variant="outline" onClick={() => setActiveTab('services')}>
                        View All <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                    
                    {/* Service filters */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      <Button 
                        size="sm"
                        variant={!selectedService ? 'default' : 'outline'}
                        onClick={() => setSelectedService(null)}
                      >
                        All
                      </Button>
                      {availableServices.slice(0, 6).map(service => (
                        <ServiceTypeCard 
                          key={service}
                          service={service}
                          onClick={handleServiceClick}
                        />
                      ))}
                    </div>
                    
                    {/* Services Grid - Different style */}
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {providers.slice(0, 6).map(provider => (
                        <ServiceCard 
                          key={provider.provider_id}
                          provider={provider}
                        />
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Ads Section */}
                {filteredAds.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl flex items-center justify-center">
                          <Star className="w-5 h-5 text-white fill-white" />
                        </div>
                        <div>
                          <h2 className="text-xl font-bold text-gray-900">Pets & Products</h2>
                          <p className="text-sm text-gray-500">Adoption, sale, and accessories</p>
                        </div>
                      </div>
                      <Button variant="outline" onClick={() => setActiveTab('ads')}>
                        View All <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                    
                    {/* Category filters for ads */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      <Button 
                        size="sm"
                        variant={!selectedCategory ? 'default' : 'outline'}
                        onClick={() => setSelectedCategory(null)}
                      >
                        All
                      </Button>
                      {categories.filter(c => c.slug !== 'pet-services').map(cat => (
                        <Button 
                          key={cat.slug}
                          size="sm"
                          variant={selectedCategory === cat.slug ? 'default' : 'outline'}
                          onClick={() => setSelectedCategory(cat.slug)}
                        >
                          {cat.icon} {cat.name}
                        </Button>
                      ))}
                    </div>
                    
                    {/* Ads Grid - Different style */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                      {filteredAds.slice(0, 10).map(ad => (
                        <AdCard key={ad.id} ad={ad} />
                      ))}
                    </div>
                  </div>
                )}
                
                {providers.length === 0 && filteredAds.length === 0 && (
                  <Card className="text-center py-12">
                    <CardContent>
                      <Dog className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                      <h3 className="text-lg font-semibold text-gray-700">No Results Found</h3>
                      <p className="text-gray-500">Try adjusting your filters or location</p>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </TabsContent>
          
          {/* Services Tab */}
          <TabsContent value="services" className="mt-6">
            {/* Service Type Filters */}
            <div className="flex flex-wrap gap-2 mb-6">
              <Button 
                variant={!selectedService ? 'default' : 'outline'}
                onClick={() => setSelectedService(null)}
              >
                All Services
              </Button>
              {availableServices.map(service => (
                <ServiceTypeCard 
                  key={service}
                  service={service}
                  onClick={handleServiceClick}
                />
              ))}
            </div>
            
            {/* Providers Grid */}
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
              </div>
            ) : providers.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Dog className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <h3 className="text-lg font-semibold text-gray-700">No Providers Found</h3>
                  <p className="text-gray-500">Try adjusting your filters or location</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {providers.map(provider => (
                  <ServiceCard 
                    key={provider.provider_id}
                    provider={provider}
                  />
                ))}
              </div>
            )}
          </TabsContent>
          
          {/* Ads Tab */}
          <TabsContent value="ads" className="mt-6">
            {/* Category Filter */}
            <div className="flex flex-wrap gap-2 mb-6">
              <Button 
                variant={!selectedCategory ? 'default' : 'outline'}
                onClick={() => setSelectedCategory(null)}
              >
                All Categories
              </Button>
              {categories.filter(c => c.slug !== 'pet-services').map(cat => (
                <Button 
                  key={cat.slug}
                  variant={selectedCategory === cat.slug ? 'default' : 'outline'}
                  onClick={() => setSelectedCategory(cat.slug)}
                >
                  {cat.icon} {cat.name}
                </Button>
              ))}
            </div>
            
            {/* Ads Grid */}
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
              </div>
            ) : filteredAds.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Heart className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <h3 className="text-lg font-semibold text-gray-700">No Ads Found</h3>
                  <p className="text-gray-500">Check back later or post your own ad</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {filteredAds.map(ad => (
                  <AdCard key={ad.id} ad={ad} />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
